import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";
import { type FC } from "react";

/**
 * Spinner variant definitions using class-variance-authority
 */
const spinnerVariants = cva("border-4 rounded-full animate-spin border-t-transparent", {
  variants: {
    size: {
      xxs: "w-2 h-2 border-2",
      xs: "w-4 h-4 border-[3px]",
      sm: "w-8 h-8",
      md: "w-16 h-16",
      lg: "w-24 h-24",
    },
    color: {
      primary: "border-blue-600",
      secondary: "border-gray-600",
      white: "border-white",
      ai: "border-violet-600",
    },
  },
  defaultVariants: {
    size: "md",
    color: "primary",
  },
});

/**
 * Props for the Spinner component
 */
export interface SpinnerProps extends VariantProps<typeof spinnerVariants> {
  /**
   * Additional CSS classes to apply to the spinner
   */
  className?: string;
  /**
   * The size of the spinner
   * @default "md"
   */
  size?: "xxs" | "xs" | "sm" | "md" | "lg";
  /**
   * The color theme of the spinner
   * @default "primary"
   */
  color?: "primary" | "secondary" | "white" | "ai";
}

/**
 * Spinner component - A loading indicator with animated spinning effect
 *
 * @component
 * @example
 * ```tsx
 * // Basic usage
 * <Spinner />
 *
 * // Small spinner with custom color
 * <Spinner size="sm" color="secondary" />
 *
 * // Large spinner with custom styling
 * <Spinner size="lg" className="border-green-500" />
 * ```
 *
 * @remarks
 * The spinner uses CSS animations and is fully customizable through size variants
 * and additional className props. It automatically includes proper ARIA attributes
 * for accessibility.
 *
 * @param props - The component props
 * @returns A spinning loading indicator with a circular border animation
 */
export const Spinner: FC<SpinnerProps> = ({ size, color, className }) => {
  return (
    <div className="relative">
      {/* Base circle (gray background) */}
      <div
        className={cn(
          spinnerVariants({ size }),
          color === "white" ? "border-transparent" : "border-gray-400",
          "border-t-gray-400"
        )}
      />

      {/* Animated spinning circle */}
      <div
        className={cn(spinnerVariants({ size, color }), "absolute top-0 left-0 border-t-transparent", className)}
        role="status"
        aria-label="Loading"
      >
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );
};
